import React, { useMemo, useRef } from 'react';
import { bool, arrayOf, func, shape } from 'prop-types';
import { Filmstrip } from '@one-thd/sui-carousel';
import { Typography } from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { MultiParentItem } from './MultiParentItem';
import { MULTI_ENTRY_POINT_BREAKPOINTS } from '../../constants/ui';
import { BundleMiniPlaceholder } from '../BundleMiniPlaceholder';
import { isBundleItemMissingInProducts, getBundleItemsFromProducts } from '../../utils/product-utils';
import { BUNDLE_PAGE_SIZE } from '../../constants/index';
import { BundleProductsDataModel } from '../../models/dataModel';
import { useMultiParentProductsFetch } from '../../hooks/useMultiParentProductsFetch';

const MultiParentCarousel = ({ bundles, bundleLoading, onError }) => {

  const {
    bundleProducts, offsetValue,
    setOffsetValue, productsLoading, uniqueItemIds,
    filteredBundles
  } = useMultiParentProductsFetch(bundles);

  const canFetchMoreProducts = useRef(offsetValue + BUNDLE_PAGE_SIZE < uniqueItemIds?.length);

  const handleSlideChange = (__, slideIndex) => {
    if (!canFetchMoreProducts?.current) {
      return;
    }
    const hasMissingProducts = isBundleItemMissingInProducts(bundleProducts, filteredBundles, slideIndex + 1);
    if (hasMissingProducts) {
      const newOffset = offsetValue + BUNDLE_PAGE_SIZE;
      setOffsetValue(newOffset);
      canFetchMoreProducts.current = newOffset + BUNDLE_PAGE_SIZE < uniqueItemIds?.length;
    }
  };

  const CarouselSlides = useMemo(() => {
    if (bundleLoading) {
      return [...Array(5)].map((__, index) => (
        <BundleMiniPlaceholder key={index} isMultiParent />
      ));
    }
    return filteredBundles?.map((bundle, index) => {
      const products = getBundleItemsFromProducts(bundleProducts, bundle);
      const loading = productsLoading
        || (isBundleItemMissingInProducts(bundleProducts, filteredBundles, index) && canFetchMoreProducts?.current);
      return (
        <MultiParentItem
          bundle={bundle}
          key={`bundle?.bundleId-${index}`}
          loading={loading}
          products={products}
        />
      );
    }) || [];
  }, [bundleProducts, bundleLoading, filteredBundles]);

  if (!filteredBundles?.length && !bundleLoading) {
    onError();
    return null;
  }

  return (
    <div
      data-testid="multi-parent-carousel"
      className="sui-bg-primary sui-mb-2 lg:sui-mt-2 lg:sui-mb-4 sui-w-full sui-pt-4 sui-pl-4"
    >
      <Typography variant="h2">Available Bundles</Typography>
      <Filmstrip
        breakpoints={MULTI_ENTRY_POINT_BREAKPOINTS}
        slidesPerView={1}
        spaceBetween={16}
        slidesPerGroup={1}
        peek="sm"
        onChange={(__, slideIndex) => { handleSlideChange(__, slideIndex); }}
      >
        {CarouselSlides}
      </Filmstrip>
    </div>
  );
};

MultiParentCarousel.propTypes = {
  bundles: arrayOf(shape({})),
  bundleLoading: bool,
  onError: func
};

MultiParentCarousel.defaultProps = {
  bundles: [],
  bundleLoading: false,
  onError: () => {}
};

MultiParentCarousel.displayName = 'MultiParentCarousel';

MultiParentCarousel.dataModel = extend(
  MultiParentItem,
  BundleProductsDataModel
);

export { MultiParentCarousel };